import React, { createContext, FC, ReactNode, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import ImageWithFocusPointSelector from '@/components/ImageWithFocusPointSelector';

/** Whether the images in a media list get a focus point selector. The banner's slideshow turns it on. */
export const MediaFocusPointContext = createContext<boolean>(false);

export interface MediaItemImageProps {
  /** The item's path in the form. The focus point is saved as local.x / local.y, like the banner's main image. */
  pathPrefix: string;
  src: string;
  title?: string;
  alt?: string;
  mimetype?: string;
  maxWidth?: string;
  /** Shown when the list has no focus point selector */
  children: ReactNode;
}

const FocusPointImage: FC<Omit<MediaItemImageProps, 'children'>> = ({ pathPrefix, src, ...imageProps }) => {
  const { setValue } = useFormContext();
  const local = useWatch({ name: `${pathPrefix}.local` }) as { x?: string; y?: string } | undefined;
  return (
    <ImageWithFocusPointSelector
      {...imageProps}
      // The selector adds its own query string (size and media type)
      src={src.split('?')[0]}
      x={local?.x || '0'}
      y={local?.y || '0'}
      onFocusPointChanged={({ x, y }) =>
        setValue(`${pathPrefix}.local`, { ...local, x, y }, { shouldDirty: true, shouldTouch: true })
      }
    />
  );
};

export const MediaItemImage: FC<MediaItemImageProps> = ({ children, ...props }) => {
  const focusPointSelectable = useContext(MediaFocusPointContext);
  if (!focusPointSelectable) return <>{children}</>;
  return <FocusPointImage {...props} />;
};

export default MediaItemImage;
